/* ═══════════════════════════════════════════════════════════════
   Smart Blocks — firm-scoped library of dynamic, context-aware spec
   content. Authors create instances ("Related Sections", custom Q&A
   tables, etc.) that resolve at project-compile time.

   Lifecycle: any firm member can draft → publisher approves → live.
   Built-in kinds (Related Sections, Section Header, Project Identity,
   Issue Date, Standards Referenced, Engineer of Record) are read-only
   reference rows that can be Cloned into a firm-editable custom block.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import {
  Plus,
  Search,
  Sparkles,
  Edit3,
  Trash2,
  Archive,
  ArchiveRestore,
  Copy as CopyIcon,
  Send,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  Save,
} from 'lucide-react';
import type {
  SmartBlock,
  SmartBlockDraft,
  SmartBlockKind,
  SmartBlockParam,
} from '../../shared/types';
import { SmartBlockService } from '../services/smartBlockService';
import { BUILT_IN_SMART_BLOCKS } from '../../engine/smart-blocks';
import { hasPermission } from '../../shared/permissions';
import {
  RungTabs,
  SmartBlockBuilderBody,
  SmartBlockPreview,
  initialRungFor,
  type RungId,
} from '../components/smart-blocks/SmartBlockBuilder';
import { Button } from '../components/ui/button';
import EmptyState from '../components/EmptyState';

/* Cross-component "smart" identity accent — same purple used for smart-block
   chips everywhere else (AddSpecs.tsx, BlockEditor.tsx). Backed by the shared
   --color-smart token so this page stays color-matched with the shared
   smart-block components rendered elsewhere. */
const SMART_ACCENT = 'var(--color-smart)';

/* Status-badge soft tints, following the color-mix convention already used in
   SpecLibrary.tsx / BlockEditor.tsx for semantic status colors. */
const SUCCESS_SOFT = 'color-mix(in srgb, var(--color-success) 16%, transparent)';
const SUCCESS_TEXT = 'color-mix(in srgb, var(--color-success) 70%, var(--tx))';
const WARN_SOFT = 'color-mix(in srgb, var(--color-warning) 16%, transparent)';
const WARN_TEXT = 'color-mix(in srgb, var(--color-warning) 60%, var(--tx))';
const INFO_SOFT = 'color-mix(in srgb, var(--color-info) 16%, transparent)';
const INFO_TEXT = 'color-mix(in srgb, var(--color-info) 70%, var(--tx))';
const NEUTRAL_SOFT = 'color-mix(in srgb, var(--tm) 20%, transparent)';

interface Props {
  toast: (msg: string, isError?: boolean) => void;
  userName?: string;
  userId?: string;
  userPermissions?: string[];
}

type View = { mode: 'list' } | { mode: 'edit'; draftId?: string; sourceSmartBlockId?: string };

export function SmartBlocksPage({ toast, userName, userId, userPermissions }: Props) {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [smartBlocks, setSmartBlocks] = useState<SmartBlock[]>([]);
  const [drafts, setDrafts] = useState<SmartBlockDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const canCreate = hasPermission(userPermissions, 'smart_blocks.create');
  const canPublish = hasPermission(userPermissions, 'smart_blocks.publish');
  const canArchive = hasPermission(userPermissions, 'smart_blocks.archive');
  const canDelete = hasPermission(userPermissions, 'smart_blocks.delete');

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const [sbsResult, drResult] = await Promise.allSettled([
      SmartBlockService.list(),
      SmartBlockService.listDrafts(),
    ]);
    if (sbsResult.status === 'fulfilled') setSmartBlocks(sbsResult.value);
    if (drResult.status === 'fulfilled') setDrafts(drResult.value);
    if (sbsResult.status === 'rejected' || drResult.status === 'rejected') {
      setLoadError('Some smart blocks failed to load.');
      toast('Failed to load smart blocks', true);
    } else {
      setLoadError(null);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    void refresh();
    const onSync = (ev: any) => {
      const tables: string[] = ev?.detail?.tables ?? [];
      if (tables.includes('smart_blocks') || tables.includes('smart_block_drafts')) {
        void refresh();
      }
    };
    window.addEventListener('specforma-sync-complete', onSync);
    return () => window.removeEventListener('specforma-sync-complete', onSync);
  }, [refresh]);

  if (view.mode === 'edit') {
    return (
      <SmartBlockEditor
        draftId={view.draftId}
        sourceSmartBlockId={view.sourceSmartBlockId}
        userName={userName ?? userId ?? 'unknown'}
        canPublish={canPublish}
        toast={toast}
        onClose={() => {
          setView({ mode: 'list' });
          void refresh();
        }}
      />
    );
  }

  return (
    <div style={{ padding: 'var(--space-5)', height: '100%', overflow: 'auto' }}>
      <SmartBlocksList
        smartBlocks={smartBlocks}
        drafts={drafts}
        loading={loading}
        loadError={loadError}
        search={search}
        setSearch={setSearch}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        canCreate={canCreate}
        canPublish={canPublish}
        canArchive={canArchive}
        canDelete={canDelete}
        userName={userName ?? userId ?? 'unknown'}
        toast={toast}
        onNewCustom={() => setView({ mode: 'edit' })}
        onEditDraft={(d) => setView({ mode: 'edit', draftId: d.id })}
        onCloneBuiltIn={async (sb) => {
          const now = new Date().toISOString();
          const draft: SmartBlockDraft = {
            id: '',
            smartBlockId: '', // new — no parent
            editedBy: userName ?? userId ?? 'unknown',
            name: `${sb.name} (Copy)`,
            description: sb.description,
            // Keep the built-in's kind so its code resolver still runs — this
            // is a firm "preset" that overrides identity + default params, NOT
            // a blank custom fork. initialRungFor() opens it in Quick.
            kind: sb.kind,
            icon: sb.icon,
            color: sb.color,
            category: sb.category,
            paramSchema: sb.paramSchema ? JSON.parse(JSON.stringify(sb.paramSchema)) : [],
            defaultParams: sb.defaultParams ? JSON.parse(JSON.stringify(sb.defaultParams)) : {},
            querySpec: undefined, // built-in kinds resolve via code, not a query
            templateSpec: '',
            createdAt: now,
            lastSaved: now,
          };
          const saved = await SmartBlockService.saveDraft(draft);
          setView({ mode: 'edit', draftId: saved.id });
          toast(`Customizing "${sb.name}" — set your firm's defaults`);
        }}
        onRefresh={refresh}
      />
    </div>
  );
}

// ─── List view ─────────────────────────────────────────────────────

interface ListProps {
  smartBlocks: SmartBlock[];
  drafts: SmartBlockDraft[];
  loading: boolean;
  loadError: string | null;
  search: string;
  setSearch: (s: string) => void;
  showArchived: boolean;
  setShowArchived: (b: boolean) => void;
  canCreate: boolean;
  canPublish: boolean;
  canArchive: boolean;
  canDelete: boolean;
  userName: string;
  toast: (msg: string, isError?: boolean) => void;
  onNewCustom: () => void;
  onEditDraft: (d: SmartBlockDraft) => void;
  onCloneBuiltIn: (sb: SmartBlock) => void;
  onRefresh: () => Promise<void>;
}

function SmartBlocksList(props: ListProps) {
  const {
    smartBlocks,
    drafts,
    loading,
    loadError,
    search,
    setSearch,
    showArchived,
    setShowArchived,
    canCreate,
    canPublish,
    canArchive,
    canDelete,
    userName,
    toast,
    onNewCustom,
    onEditDraft,
    onCloneBuiltIn,
    onRefresh,
  } = props;

  // Merge built-ins + firm published into a single browsing list.
  const allRows = useMemo(() => {
    type Row =
      | { kind: 'builtin'; sb: SmartBlock }
      | { kind: 'firm'; sb: SmartBlock; myDraft?: SmartBlockDraft }
      | { kind: 'draft'; draft: SmartBlockDraft };

    const rows: Row[] = [];
    for (const sb of BUILT_IN_SMART_BLOCKS) rows.push({ kind: 'builtin', sb });
    for (const sb of smartBlocks) {
      if (!showArchived && sb.status === 'archived') continue;
      const myDraft = drafts.find((d) => d.smartBlockId === sb.id && d.editedBy === userName);
      rows.push({ kind: 'firm', sb, myDraft });
    }
    // Drafts WITHOUT a published parent (= new custom in progress)
    for (const d of drafts) {
      if (!d.smartBlockId) rows.push({ kind: 'draft', draft: d });
    }
    return rows;
  }, [smartBlocks, drafts, userName, showArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => {
      const sb = r.kind === 'draft' ? r.draft : r.sb;
      const name = (sb.name ?? '').toLowerCase();
      const desc = (sb.description ?? '').toLowerCase();
      const cat = (sb.category ?? '').toLowerCase();
      const kind = (sb.kind ?? '').toLowerCase();
      return name.includes(q) || desc.includes(q) || cat.includes(q) || kind.includes(q);
    });
  }, [allRows, search]);

  return (
    <>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-4)',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: -0.2 }}>
            Smart Blocks
          </h2>
          <p
            style={{
              margin: '4px 0 0',
              fontSize: 13,
              color: 'var(--tm)',
              lineHeight: 1.45,
              maxWidth: 600,
            }}
          >
            Dynamic content that fills in from project data at compile time. Drop one into a master
            spec and it stays current as the project changes.
          </p>
        </div>
        {canCreate && (
          <Button onClick={onNewCustom}>
            <Plus size={14} strokeWidth={2.5} />
            New Custom
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          padding: 'var(--space-2) var(--space-3)',
          background: 'var(--sa)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--bd)',
        }}
      >
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: '4px var(--space-2)',
            background: 'var(--sf)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--bd)',
          }}
        >
          <Search size={14} color="var(--tm)" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search smart blocks…"
            aria-label="Search smart blocks"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13,
              color: 'var(--tx)',
            }}
          />
        </div>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            color: 'var(--tm)',
            cursor: 'pointer',
            padding: '4px var(--space-2)',
          }}
        >
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {loadError && (
        <div
          role="alert"
          style={{
            fontSize: 13,
            color: 'var(--dg)',
            padding: '0 0 var(--space-2)',
          }}
        >
          {loadError} Check your connection and try again shortly — this list refreshes
          automatically once data syncs.
        </div>
      )}

      {/* Gallery */}
      {loading ? (
        <div
          aria-hidden="true"
          className="animate-pulse"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(244px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              style={{
                minHeight: 152,
                borderRadius: 'var(--radius-lg)',
                background: 'var(--sa)',
              }}
            />
          ))}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(244px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          {canCreate && (
            <button
              onClick={onNewCustom}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                e.currentTarget.style.borderColor = 'rgba(var(--acr),0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--bd)';
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 'var(--space-2)',
                minHeight: 152,
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-lg)',
                border: '1px dashed var(--bd)',
                background: 'var(--sf)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all var(--duration-fast) var(--ease-out)',
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 42,
                  height: 42,
                  borderRadius: 'var(--radius-lg)',
                  background: 'rgba(var(--acr),0.12)',
                  color: 'var(--ac)',
                }}
              >
                <Plus size={20} strokeWidth={2.2} />
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--tx)' }}>
                Build from scratch
              </span>
              <span style={{ fontSize: 11, color: 'var(--tm)', lineHeight: 1.45 }}>
                Compose a new block in the visual builder — pick a source, add filters, choose a
                layout. No code.
              </span>
            </button>
          )}
          {filtered.map((r, i) => (
            <SmartBlockRow
              key={i}
              row={r}
              canCreate={canCreate}
              canPublish={canPublish}
              canArchive={canArchive}
              canDelete={canDelete}
              userName={userName}
              toast={toast}
              onEditDraft={onEditDraft}
              onCloneBuiltIn={onCloneBuiltIn}
              onRefresh={onRefresh}
            />
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <EmptyState
                icon={<Sparkles size={28} />}
                title="No smart blocks match your search"
                subtitle="Try a different name, category, or kind."
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

function SmartBlockRow({
  row,
  canCreate,
  canPublish,
  canArchive,
  canDelete,
  userName,
  toast,
  onEditDraft,
  onCloneBuiltIn,
  onRefresh,
}: {
  row:
    | { kind: 'builtin'; sb: SmartBlock }
    | { kind: 'firm'; sb: SmartBlock; myDraft?: SmartBlockDraft }
    | { kind: 'draft'; draft: SmartBlockDraft };
  canCreate: boolean;
  canPublish: boolean;
  canArchive: boolean;
  canDelete: boolean;
  userName: string;
  toast: (msg: string, isError?: boolean) => void;
  onEditDraft: (d: SmartBlockDraft) => void;
  onCloneBuiltIn: (sb: SmartBlock) => void;
  onRefresh: () => Promise<void>;
}) {
  const sb = row.kind === 'draft' ? null : row.sb;
  const draft = row.kind === 'draft' ? row.draft : row.kind === 'firm' ? row.myDraft : undefined;
  const name = (sb ?? draft)?.name ?? 'Untitled';
  const iconName = (sb ?? draft)?.icon ?? 'Sparkles';
  const color = (sb ?? draft)?.color ?? '#228be6';
  const category = (sb ?? draft)?.category ?? 'Other';
  const kind = (sb ?? draft)?.kind ?? 'custom';
  const version = sb?.version ?? '—';
  const status: string =
    row.kind === 'builtin'
      ? 'built-in'
      : row.kind === 'draft'
        ? 'new draft'
        : (sb?.status ?? 'published');
  // @ts-ignore lucide
  const Icon = (LucideIcons[iconName] as any) ?? LucideIcons.Sparkles;

  async function handleCheckout() {
    if (!sb) return;
    try {
      const newDraft = await SmartBlockService.checkout(sb.id, userName);
      onEditDraft(newDraft);
    } catch (err: any) {
      toast(`Couldn't check out: ${err.message ?? err}`, true);
    }
  }

  async function handleArchive() {
    if (!sb) return;
    if (!confirm(`Archive "${sb.name}"? It will no longer appear in the picker.`)) return;
    try {
      await SmartBlockService.archive(sb, userName);
      toast(`Archived "${sb.name}"`);
      await onRefresh();
    } catch (err: any) {
      toast(`Archive failed: ${err.message}`, true);
    }
  }

  async function handleRestore() {
    if (!sb) return;
    try {
      await SmartBlockService.restore(sb);
      toast(`Restored "${sb.name}"`);
      await onRefresh();
    } catch (err: any) {
      toast(`Restore failed: ${err.message}`, true);
    }
  }

  async function handleDelete() {
    if (!sb) return;
    if (!confirm(`Delete "${sb.name}" permanently? Existing references will go unresolved.`))
      return;
    try {
      await SmartBlockService.delete(sb.id);
      toast(`Deleted "${sb.name}"`);
      await onRefresh();
    } catch (err: any) {
      toast(`Delete failed: ${err.message}`, true);
    }
  }

  async function handleDiscardDraft() {
    if (!draft) return;
    if (!confirm(`Discard your draft of "${draft.name ?? 'this smart block'}"?`)) return;
    try {
      await SmartBlockService.deleteDraft(draft.id);
      toast('Draft discarded');
      await onRefresh();
    } catch (err: any) {
      toast(`Discard failed: ${err.message}`, true);
    }
  }

  return (
    <div
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
        e.currentTarget.style.borderColor = `${color}66`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = 'var(--bd)';
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        minHeight: 152,
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        background: 'var(--sf)',
        transition: 'all var(--duration-fast) var(--ease-out)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 42,
            height: 42,
            borderRadius: 'var(--radius-lg)',
            background: `${color}22`,
            color,
            flexShrink: 0,
          }}
        >
          <Icon size={20} strokeWidth={1.8} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--tx)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
            {kind}
            {category && category !== 'Other' ? ` · ${category}` : ''}
            {version !== '—' ? ` · v${version}` : ''}
          </div>
        </div>
        <StatusBadge status={status} />
      </div>

      <div
        style={{
          flex: 1,
          fontSize: 11,
          color: 'var(--tm)',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {(sb?.description ?? draft?.description) || 'No description.'}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {row.kind === 'builtin' && canCreate && (
          <ActionBtn
            icon={<CopyIcon size={11} />}
            label="Customize"
            onClick={() => onCloneBuiltIn(row.sb)}
            primary
          />
        )}
        {row.kind === 'draft' && (
          <>
            <ActionBtn
              icon={<Edit3 size={11} />}
              label="Continue"
              onClick={() => onEditDraft(row.draft)}
              primary
            />
            <ActionBtn
              icon={<XCircle size={11} />}
              label="Discard"
              onClick={handleDiscardDraft}
              danger
            />
          </>
        )}
        {row.kind === 'firm' && (
          <>
            {row.myDraft ? (
              <ActionBtn
                icon={<Edit3 size={11} />}
                label="Resume Draft"
                onClick={() => onEditDraft(row.myDraft!)}
                primary
              />
            ) : (
              <ActionBtn icon={<Edit3 size={11} />} label="Edit" onClick={handleCheckout} primary />
            )}
            {sb?.status !== 'archived' && canArchive && (
              <ActionBtn icon={<Archive size={11} />} label="Archive" onClick={handleArchive} />
            )}
            {sb?.status === 'archived' && canArchive && (
              <ActionBtn
                icon={<ArchiveRestore size={11} />}
                label="Restore"
                onClick={handleRestore}
              />
            )}
            {canDelete && (
              <ActionBtn icon={<Trash2 size={11} />} label="Delete" onClick={handleDelete} danger />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    'built-in': {
      bg: 'color-mix(in srgb, var(--color-smart) 13%, transparent)',
      color: SMART_ACCENT,
      label: 'Built-in',
    },
    published: { bg: SUCCESS_SOFT, color: SUCCESS_TEXT, label: 'Published' },
    draft: { bg: WARN_SOFT, color: WARN_TEXT, label: 'Draft' },
    'new draft': { bg: WARN_SOFT, color: WARN_TEXT, label: 'New Draft' },
    pending_review: { bg: INFO_SOFT, color: INFO_TEXT, label: 'Pending Review' },
    archived: { bg: NEUTRAL_SOFT, color: 'var(--tm)', label: 'Archived' },
  };
  const m = map[status] ?? map.draft;
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-md)',
        background: m.bg,
        color: m.color,
      }}
    >
      {m.label}
    </span>
  );
}

function ActionBtn({
  icon,
  label,
  onClick,
  primary,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={(e) => {
        if (primary) {
          e.currentTarget.style.filter = 'brightness(0.94)';
        } else {
          e.currentTarget.style.background = danger
            ? 'color-mix(in srgb, var(--color-danger) 8%, transparent)'
            : 'var(--sa)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.filter = 'none';
        e.currentTarget.style.background = primary ? 'var(--ac)' : 'var(--sf)';
      }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 'var(--space-1) var(--space-2)',
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 'var(--radius-md)',
        border: primary ? 'none' : '1px solid var(--bd)',
        background: primary ? 'var(--ac)' : 'var(--sf)',
        color: primary ? 'var(--color-text-inverse)' : danger ? 'var(--dg)' : 'var(--tx)',
        cursor: 'pointer',
        transition: 'background var(--duration-fast), filter var(--duration-fast)',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

// ─── Editor view ─────────────────────────────────────────────────────

function SmartBlockEditor({
  draftId,
  sourceSmartBlockId,
  userName,
  canPublish,
  toast,
  onClose,
}: {
  draftId?: string;
  sourceSmartBlockId?: string;
  userName: string;
  canPublish: boolean;
  toast: (msg: string, isError?: boolean) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<SmartBlockDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rung, setRung] = useState<RungId>('build');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (draftId) {
          const d = await SmartBlockService.getDraft(draftId);
          if (!cancel) {
            setDraft(d);
            if (d) setRung(initialRungFor(d));
          }
        } else {
          // Fresh new custom smart block
          const now = new Date().toISOString();
          if (!cancel) {
            setDraft({
              id: '',
              smartBlockId: '',
              editedBy: userName,
              name: 'Untitled Smart Block',
              description: '',
              kind: 'custom',
              icon: 'Sparkles',
              color: '#228be6',
              category: 'Custom',
              paramSchema: [],
              defaultParams: {},
              querySpec: { from: 'project-specs' },
              templateSpec: '',
              createdAt: now,
              lastSaved: now,
            });
            setDirty(true);
            setRung('build');
          }
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [draftId, userName]);

  function update<K extends keyof SmartBlockDraft>(key: K, val: SmartBlockDraft[K]) {
    setDraft((d) => (d ? { ...d, [key]: val } : d));
    setDirty(true);
  }

  function patch(partial: Partial<SmartBlockDraft>) {
    setDraft((d) => (d ? { ...d, ...partial } : d));
    setDirty(true);
  }

  async function handleSave() {
    if (!draft) return;
    setSaving(true);
    try {
      const saved = await SmartBlockService.saveDraft({
        ...draft,
        lastSaved: new Date().toISOString(),
      });
      setDraft(saved);
      setDirty(false);
      toast('Draft saved');
    } catch (err: any) {
      toast(`Save failed: ${err.message}`, true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit() {
    if (!draft) return;
    if (dirty) await handleSave();
    setSaving(true);
    try {
      await SmartBlockService.submitForReview(draft, userName);
      toast('Submitted for review');
      onClose();
    } catch (err: any) {
      toast(`Submit failed: ${err.message}`, true);
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!draft) return;
    if (dirty) await handleSave();
    if (!canPublish) {
      toast('You need publish permission for smart blocks', true);
      return;
    }
    setSaving(true);
    try {
      await SmartBlockService.approveDraft(draft, userName, userName);
      toast('Published');
      onClose();
    } catch (err: any) {
      toast(`Publish failed: ${err.message}`, true);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div
        aria-hidden="true"
        className="animate-pulse"
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <div
          style={{
            height: 57,
            borderBottom: '1px solid var(--bd)',
            background: 'var(--sa)',
          }}
        />
        <div style={{ flex: 1, display: 'flex', gap: 'var(--space-3)', padding: 'var(--space-5)' }}>
          <div style={{ flex: 1, borderRadius: 'var(--radius-md)', background: 'var(--sa)' }} />
          <div style={{ width: 360, borderRadius: 'var(--radius-md)', background: 'var(--sa)' }} />
        </div>
      </div>
    );
  }
  if (!draft) {
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <div style={{ marginBottom: 'var(--space-3)' }}>Draft not found.</div>
        <Button variant="outline" size="sm" onClick={onClose}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-5)',
          borderBottom: '1px solid var(--bd)',
          background: 'var(--sa)',
        }}
      >
        <Button variant="outline" size="sm" onClick={onClose}>
          <ChevronLeft size={14} />
          Back
        </Button>
        <input
          value={draft.name ?? ''}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Smart Block Name"
          aria-label="Smart block name"
          style={{
            flex: 1,
            fontSize: 18,
            fontWeight: 700,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--tx)',
          }}
        />
        <span
          style={{
            fontSize: 10,
            color: dirty ? 'var(--color-warning)' : 'var(--tm)',
            fontWeight: 600,
          }}
        >
          {dirty ? 'Unsaved' : 'Saved'}
        </span>
        <Button variant="outline" size="sm" disabled={saving} onClick={handleSave}>
          <Save size={13} />
          Save Draft
        </Button>
        {canPublish ? (
          <Button size="sm" disabled={saving} onClick={handlePublish}>
            <CheckCircle2 size={13} />
            Publish
          </Button>
        ) : (
          <Button size="sm" disabled={saving} onClick={handleSubmit}>
            <Send size={13} />
            Submit for Review
          </Button>
        )}
      </div>

      {/* Rung tabs */}
      <div
        style={{
          padding: 'var(--space-3) var(--space-5) 0',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <RungTabs rung={rung} onChange={setRung} />
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Builder */}
        <div style={{ flex: 1, padding: 'var(--space-4) var(--space-5)', overflowY: 'auto' }}>
          <div style={{ maxWidth: 760 }}>
            <SmartBlockBuilderBody rung={rung} draft={draft} update={update} patch={patch} />
          </div>
        </div>

        {/* Preview */}
        <div
          style={{
            width: 360,
            borderLeft: '1px solid var(--bd)',
            padding: 'var(--space-5) var(--space-4)',
            background: 'var(--sa)',
            overflowY: 'auto',
          }}
        >
          <SmartBlockPreview draft={draft} />
        </div>
      </div>
    </div>
  );
}

// The editor body (Quick / Build / Advanced rungs + live preview) now lives
// in components/smart-blocks/SmartBlockBuilder.tsx.
